// components/auth/AuthForm.tsx
"use client";

import { useState } from "react";
import { Mail, Lock, User, Eye, EyeOff } from "lucide-react";
import { authService } from "@cabukaka/services";
import { useAuth, useToken } from "@cabukaka/hooks";
import GoogleLogo from "../../ui/GoogleLogo";
import { Link, useNavigate } from "react-router-dom";

interface AuthFormProps {
  type: "login" | "register";
}

export default function AuthForm({ type }: AuthFormProps) {
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { saveToken } = useToken();
  const { setUser } = useAuth()
  const navigate = useNavigate();


  const [formData, setFormData] = useState({
    name: "",
    email: "",
    password: "",
  });

  const isLogin = type === "login";

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setError(null);

    const { email, password, name } = formData;

    try {
      if (isLogin) {
        const response = await authService.login({ email, password });
        const token = response.token;
        if (token) {
          saveToken(token);

          const me = await authService.getUserProfile(token)
          if (me) {
            setUser(me)
            navigate(-1);

          }
        }
      } else {
        const response = await authService.register({
          email,
          password,
          username: name,
          role: "client",
          profileData: {
            name,
            email
          }
        });
        const token = response.token;
        if (token) {
          saveToken(token);

          const me = await authService.getUserProfile(token)
          if (me) {
            setUser(me)
            navigate(-1);

          }
        }
      }
    } catch (err: any) {
      setError(err.message || "Une erreur est survenue");
    } finally {
      setIsLoading(false);
    }
  };

  const handleGoogleLogin = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const response = await authService.loginWithGoogle({ role: "client" });
      const token = response.token;
      if (token) {
        saveToken(token);

        const me = await authService.getUserProfile(token)
        if (me) {
          setUser(me)
          navigate(-1);

        }
      }
    } catch (err: any) {
      setError(err.message || "Erreur lors de la connexion avec Google");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="bg-white rounded-2xl shadow-xl p-6 md:p-8">
      {/* Titre */}
      <div className="text-center mb-8">
        <h1 className="text-2xl md:text-3xl font-bold text-gray-900">
          {isLogin ? "Connexion" : "Inscription"}
        </h1>
        <p className="text-gray-600 mt-2 text-sm">
          {isLogin
            ? "Connectez-vous à votre compte"
            : "Créez votre compte gratuitement"}
        </p>
      </div>

      {/* Message d'erreur */}
      {error && (
        <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-red-600 text-sm">
          {error}
        </div>
      )}

      {/* Formulaire */}
      <form onSubmit={handleSubmit} className="space-y-4">
        {/* Champ Nom - Uniquement pour inscription */}
        {!isLogin && (
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Nom complet
            </label>
            <div className="relative">
              <User className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
              <input
                type="text"
                placeholder="Jean Dupont"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                required={!isLogin}
              />
            </div>
          </div>
        )}

        {/* Champ Email */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Email
          </label>
          <div className="relative">
            <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
            <input
              type="email"
              placeholder="exemple@email.com"
              value={formData.email}
              onChange={(e) => setFormData({ ...formData, email: e.target.value })}
              className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent"
              required
            />
          </div>
        </div>

        {/* Champ Password */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Mot de passe
          </label>
          <div className="relative">
            <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
            <input
              type={showPassword ? "text" : "password"}
              placeholder="••••••••"
              value={formData.password}
              onChange={(e) => setFormData({ ...formData, password: e.target.value })}
              className="w-full pl-10 pr-12 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent"
              required
            />
            <button
              type="button"
              onClick={() => setShowPassword(!showPassword)}
              className="absolute right-3 top-1/2 -translate-y-1/2"
            >
              {showPassword ? (
                <EyeOff className="w-5 h-5 text-gray-400" />
              ) : (
                <Eye className="w-5 h-5 text-gray-400" />
              )}
            </button>
          </div>
        </div>

        {/* Mot de passe oublié - uniquement login */}
        {isLogin && (
          <div className="text-right">
            <Link
              to="/mot-de-passe-oublie"
              className="text-sm text-orange-600 hover:text-orange-700"
            >
              Mot de passe oublié ?
            </Link>
          </div>
        )}

        {/* Bouton submit */}
        <button
          type="submit"
          disabled={isLoading}
          className="w-full bg-orange-500 hover:bg-orange-600 text-white font-semibold py-3 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {isLoading
            ? "Chargement..."
            : isLogin
              ? "Se connecter"
              : "S'inscrire"}
        </button>
      </form>

      {/* Séparateur */}
      <div className="relative my-6">
        <div className="absolute inset-0 flex items-center">
          <div className="w-full border-t border-gray-300"></div>
        </div>
        <div className="relative flex justify-center text-sm">
          <span className="px-4 bg-white text-gray-500">ou</span>
        </div>
      </div>

      {/* Bouton Google */}
      <button
        onClick={handleGoogleLogin}
        disabled={isLoading}
        className="w-full flex items-center justify-center gap-3 border border-gray-300 hover:bg-gray-50 text-gray-700 font-medium py-3 rounded-lg transition-colors disabled:opacity-50"
      >
        <GoogleLogo />
        <span>Continuer avec Google</span>
      </button>

      {/* Lien basculer entre login/register */}
      <p className="text-center text-sm text-gray-600 mt-6">
        {isLogin ? "Pas encore de compte ? " : "Déjà un compte ? "}
        <Link
          to={isLogin ? "/auth/register" : "/auth/login"}
          className="text-orange-600 hover:text-orange-700 font-semibold"
        >
          {isLogin ? "S'inscrire" : "Se connecter"}
        </Link>
      </p>
    </div>
  );
}